import { useContext, useEffect, useMemo, useState } from 'react';
import { useRemote } from '../../../Utils/Utils';
import { Download, Eye } from 'react-feather';
import { useHistory } from 'react-router-dom';
import { BannerContext } from '../../../InternalApiApp';
import Table from '../../../components/Table/Table';
import Tooltip from '../../../components/popups/Tooltip';
import LoadingIcon from '../../Shared/LoadingIcon';
import { analyticsExportFilename } from '../AnalyticsUtils';
import moment from 'moment';


const TopManager = ({ timeFrame, customeDate, onExport }) => {
	const remote = useRemote();
	const history = useHistory()
	const { bannerAdditionalInfo } = useContext(BannerContext)

	const [loading, setLoading] = useState(true);
	const [data, setData] = useState([])

	useEffect(() => {
		if (bannerAdditionalInfo && customeDate && customeDate.from && customeDate.to) {
			setLoading(true);
			const end = moment(customeDate.to).format('YYYY-MM-DD');
			const start = moment(customeDate.from).format('YYYY-MM-DD');
			const products = bannerAdditionalInfo?.platforms.map(platform =>
				platform.products.map(p => p.selected ? `product=${p.id}` : null)
					.filter(e => e !== null)
					.join("&")
			)

			const viewsPromise = remote.get(`/analytics/aggregates/groups/rankings/views?${products ? products + "&" : ""}from=${start}&to=${end}&scope=Manager`)
			const downloadsPromise = remote.get(`/analytics/aggregates/groups/rankings/downloads?${products ? products + "&" : ""}from=${start}&to=${end}&scope=Manager`)

			Promise.all([viewsPromise, downloadsPromise]).then(([viewsRankings, downloadsRankings]) => {
				const downloadMap = downloadsRankings.map(item => {
					return { name: item.name, link: item.link, downloads: item.count }
				})

				const rankingsMap = viewsRankings.map(item => {
					return { name: item.name, link: item.link, views: item.count }
				})

				const rankingsArr = [...downloadMap, ...rankingsMap];
				const rankings = rankingsArr.reduce((acc, current) => {
					const existing = acc.find(item => item.link === current.link);
					if (existing) {
						existing.views = (existing.views || 0) + (current.views || 0);
						existing.downloads = (existing.downloads || 0) + (current.downloads || 0);
					} else {
						acc.push({ ...current, views: current.views || 0, downloads: current.downloads || 0 });
					}
					return acc;
				}, []);

				rankingsArr.sort((a, b) => b.views - a.views);
				setData(rankings)
				setLoading(false);
			}).catch(() => setLoading(false));
		}
	}, [timeFrame, customeDate, bannerAdditionalInfo, remote])

	const exportHeaders = useMemo(() => [
		{ label: 'Name', key: 'name' },
		{ label: 'Views', key: 'views' },
		{ label: 'Downloads', key: 'downloads' },
	], []);

	const exportFilename = useMemo(
		() => analyticsExportFilename('top-managers-table', customeDate),
		[customeDate]
	);

	useEffect(() => {
		if (onExport) onExport({ headers: exportHeaders, data, filename: exportFilename, loading });
	}, [onExport, exportHeaders, data, exportFilename, loading]);

	return (
		<ManagerTable data={data} changeRoute={(url) => history.push(url)} loading={loading}/>
	)
}

const ManagerTable = ({ data, changeRoute, loading }) => {
	const [sortingColumn, setSortingColumn] = useState('views')
	const colWidths = ["90%", "70px", "70px"]
	const headers = [
		{
			text: "Name",
			className: 'compact-header'
		},
		{
			text: (
				<Tooltip label={"Views"}>
					<Eye className={`cursor-pointer ${sortingColumn === 'views' ? "colour-text-primary" : 'colour-text-neutral-dark-30'}`} size={16} onClick={() => toggleSort("views")} />
				</Tooltip>
			),
			className: 'text-align-right compact-header'
		},
		{
			text: (
				<Tooltip label={"Downloads"}>
					<Download className={`cursor-pointer ${sortingColumn === 'downloads' ? "colour-text-positive" : 'colour-text-neutral-dark-30'}`} size={16} onClick={() => toggleSort("downloads")} />
				</Tooltip>
			),
			className: 'text-align-right compact-header'
		}
	]

	const dataMap = () => data && data.map((manager, idx) => {
		if (idx > 4) return null;
		return (
			<tr key={idx} className="cursor-pointer" onClick={() => changeRoute(`/analytics/managers/${manager.link}`)}>
				<td style={{ fontSize: '0.85rem' }}>{manager.name}</td>
				<td style={{ fontSize: '0.85rem', textAlign: 'right' }}>{manager.views}</td>
				<td style={{ fontSize: '0.85rem', textAlign: 'right' }}>{manager.downloads}</td>
			</tr>
		);
	});

	const sortTable = (dataToSort) => {
		if (sortingColumn === null) {
			return dataToSort
		}
		if (!dataToSort) {
			return dataToSort
		}

		let tableData = dataToSort
		const column = sortingColumn;

		tableData.sort((a, b) => {
			const element1 = a[column]
			const element2 = b[column]
			if (!element1) {
				return 1;
			}
			if (!element2) {
				return -1;
			}
			return element2 - element1
		})
		return tableData
	}

	const toggleSort = (column) => {
		if (column === null) {
			setSortingColumn("views")
			return
		}
		setSortingColumn(column)
	}

	const sortedDataToDisplay = sortTable(data)
	const dataToDisplay = !!sortedDataToDisplay ? dataMap(sortedDataToDisplay) : null


	return (
		loading
			? <div style={{ height: 193, display: 'flex' }}><LoadingIcon centered /></div>
			: <Table suggestionMessage smStyle={{ height: 193 }} rowSize="s" colWidths={colWidths} headers={headers} dataMap={dataToDisplay} />
	)
}

export default TopManager;